import { parseCost, type Cost } from './resources.js';
import { UNIT_DESCRIPTIONS } from './unit-descriptions.js';

export const UNIT_CLASSES = ['INF', 'BLD', 'ART', 'HEL', 'AER', 'NAV', 'SUB'] as const;
export type UnitClass = (typeof UNIT_CLASSES)[number];

export type Detectability = 'A' | 'M' | 'B';

export const UPGRADE_GROUPS = ['infantry', 'armor', 'artillery', 'aviation', 'navy', 'submarines'] as const;
export type UpgradeGroup = (typeof UPGRADE_GROUPS)[number];

export type UnitBranch =
  'army' | 'air' | 'helicopter' | 'naval-aviation' | 'navy' | 'intel' | 'missile' | 'space' | 'neutral';

/**
 * Onde a unidade vive e se move:
 * - land: territórios terrestres (exército e helicópteros);
 * - air: baseada numa Base aérea / porta-aviões, age por missões;
 * - sea: zonas marítimas (e portos);
 * - none: consumíveis, satélites e agentes (ficam no território de produção).
 */
export type UnitDomain = 'land' | 'air' | 'sea' | 'none';

export interface UnitCapacity {
  /** Soldados/engenheiros (INF). */
  troops?: number;
  /** Veículos (BLD/ART). */
  vehicles?: number;
  resources?: number;
  /** Aeronaves embarcadas (porta-aviões). */
  aircraft?: number;
  /** Helicópteros antissubmarino embarcados. */
  asw?: number;
  /** Mísseis embarcados (SSBN, cruzador, submarino nuclear): ver `missileLoad`. */
  missiles?: number;
  /** Ogivas nucleares embarcadas (cada uma vai num dos mísseis a bordo). */
  warheads?: number;
}

export interface UnitDef {
  id: string;
  name: string;
  /** Função da unidade (ajuda ⓘ), de `unit-descriptions.ts`. */
  description: string;
  /** Abreviação militar exibida no mapa (INF, BLD, CT...). */
  abbr: string;
  branch: UnitBranch;
  domain: UnitDomain;
  era: 1 | 2 | 3 | 4;
  unitClass: UnitClass | null;
  atk: number;
  def: number;
  /** Movimento por turno (territórios ou zonas marítimas). */
  move: number;
  /** Alcance da missão em territórios a partir da base. `null` = ilimitado. */
  range?: number | null;
  /** Alcance com rota sem volta (destino final em outra base). */
  rangeOneWay?: number;
  detectability?: Detectability;
  bombingPower?: number;
  cost: Cost;
  /** Manutenção em dinheiro por turno. */
  upkeep: { money: number };
  producedAt: readonly string[];
  buildMinutes: number;
  /** Pesquisa que libera a unidade (`null` = conhecida no início). */
  research: string | null;
  /** Substitui a linha da matriz de multiplicadores (classes ausentes = 0). */
  multipliers?: Partial<Record<UnitClass, number>>;
  /** Carga de saque. */
  loot?: number;
  capacity?: UnitCapacity;
  /** Cobertura antiaérea passiva somada ao território onde está. */
  airDefenseCoverage?: number;
  upgradeGroup?: UpgradeGroup;
  /** Único tipo que conquista território terrestre. */
  conquers?: boolean;
  /** Navio de linha: controla zona marítima. */
  lineShip?: boolean;
  /** Não causa dano em combate (só se defende). */
  noDamage?: boolean;
  recon?: boolean;
  fighter?: boolean;
  bomber?: boolean;
  cargo?: 'resources' | 'units';
  /** Aviação naval: opera de porta-aviões. */
  carrierCapable?: boolean;
  /** Sempre detecta submarinos na mesma zona. */
  detectsSubmarines?: boolean;
  /** Pode levar ogiva nuclear. */
  nuclearCapable?: boolean;
  /** Míssil que o navio embarca nos slots de `capacity.missiles` (Frota › embarcar). */
  missileLoad?: string;
  /** Consumível (míssil, ogiva): gasto no uso. */
  consumable?: boolean;
  /** Vida útil em turnos (satélites). */
  lifetimeTurns?: number;
  /** Símbolo APP-6/MIL-STD-2525C (15 caracteres); o 2º caractere (afiliação) é trocado na renderização. */
  sidc: string;
}

type UnitInput = Omit<UnitDef, 'cost' | 'upkeep' | 'description'> & {
  cost: string;
  upkeep?: Partial<UnitDef['upkeep']>;
};

function u(input: UnitInput): UnitDef {
  return {
    ...input,
    description: UNIT_DESCRIPTIONS[input.id] ?? '',
    cost: parseCost(input.cost),
    upkeep: { money: input.upkeep?.money ?? 0 },
  };
}

const MIN = 1;
const H = 60 * MIN;

export const UNITS: readonly UnitDef[] = [
  // ─── Exército (Quartel) ───────────────────────────────────────────────
  u({
    id: 'soldier',
    name: 'Soldado',
    abbr: 'INF',
    branch: 'army',
    domain: 'land',
    era: 1,
    unitClass: 'INF',
    atk: 10,
    def: 12,
    move: 1,
    cost: '10 ALI · 5 $',
    producedAt: ['barracks'],
    buildMinutes: 10 * MIN,
    research: null,
    loot: 5,
    conquers: true,
    upgradeGroup: 'infantry',
    sidc: 'SFGPUCI--------',
  }),
  u({
    id: 'truck',
    name: 'Caminhão de transporte',
    abbr: 'TRP',
    branch: 'army',
    domain: 'land',
    era: 1,
    unitClass: 'BLD',
    atk: 0,
    def: 4,
    move: 2,
    cost: '20 MAD · 10 FER · 15 $',
    upkeep: { money: 1 },
    producedAt: ['barracks'],
    buildMinutes: 30 * MIN,
    research: 'motorization',
    loot: 50,
    noDamage: true,
    capacity: { troops: 10 },
    upgradeGroup: 'armor',
    sidc: 'SFGPUST--------',
  }),
  u({
    id: 'artillery',
    name: 'Artilharia',
    abbr: 'ART',
    branch: 'army',
    domain: 'land',
    era: 1,
    unitClass: 'ART',
    atk: 18,
    def: 6,
    move: 1,
    cost: '20 MAD · 20 FER · 20 $',
    upkeep: { money: 1 },
    producedAt: ['barracks'],
    buildMinutes: 30 * MIN,
    research: 'field-artillery',
    upgradeGroup: 'artillery',
    sidc: 'SFGPUCF--------',
  }),
  u({
    id: 'recon-armored',
    name: 'Blindado de reconhecimento',
    abbr: 'REC',
    branch: 'army',
    domain: 'land',
    era: 1,
    unitClass: 'BLD',
    atk: 4,
    def: 6,
    move: 2,
    cost: '10 AÇO · 20 $',
    upkeep: { money: 1 },
    producedAt: ['barracks'],
    buildMinutes: 30 * MIN,
    research: 'motorized-recon',
    loot: 10,
    recon: true,
    upgradeGroup: 'armor',
    sidc: 'SFGPUCR--------',
  }),
  u({
    id: 'tank',
    name: 'Tanque',
    abbr: 'BLD',
    branch: 'army',
    domain: 'land',
    era: 1,
    unitClass: 'BLD',
    atk: 20,
    def: 16,
    move: 2,
    cost: '25 AÇO · 40 $',
    upkeep: { money: 2 },
    producedAt: ['barracks'],
    buildMinutes: 30 * MIN,
    research: 'armor',
    loot: 10,
    upgradeGroup: 'armor',
    sidc: 'SFGPUCA--------',
  }),
  u({
    id: 'engineers',
    name: 'Engenheiros',
    abbr: 'ENG',
    branch: 'army',
    domain: 'land',
    era: 2,
    unitClass: 'INF',
    atk: 4,
    def: 8,
    move: 1,
    cost: '10 ALI · 10 AÇO · 20 $',
    producedAt: ['barracks'],
    buildMinutes: 10 * MIN,
    research: 'combat-engineering',
    upgradeGroup: 'infantry',
    sidc: 'SFGPUCE--------',
  }),
  u({
    id: 'mobile-aa',
    name: 'Antiaéreo móvel',
    abbr: 'AAA',
    branch: 'army',
    domain: 'land',
    era: 2,
    unitClass: 'ART',
    atk: 2,
    def: 10,
    move: 1,
    cost: '20 AÇO · 30 $',
    upkeep: { money: 1 },
    producedAt: ['barracks'],
    buildMinutes: 30 * MIN,
    research: 'air-defense',
    multipliers: { AER: 3, HEL: 3 },
    airDefenseCoverage: 3,
    upgradeGroup: 'artillery',
    sidc: 'SFGPUCD--------',
  }),
  u({
    id: 'mobile-missile-launcher',
    name: 'Lança-mísseis móvel',
    abbr: 'LMM',
    branch: 'army',
    domain: 'land',
    era: 3,
    unitClass: 'ART',
    atk: 25,
    def: 8,
    move: 1,
    range: 3,
    detectability: 'B',
    bombingPower: 30,
    cost: '25 AÇO · 10 ALU · 15 SEM · 5 CMB · 80 $',
    upkeep: { money: 1 },
    producedAt: ['barracks'],
    buildMinutes: 2 * H,
    research: 'tactical-missiles',
    consumable: true,
    upgradeGroup: 'artillery',
    sidc: 'SFGPUCFR-------',
  }),

  // ─── Aeronáutica (Base aérea) ─────────────────────────────────────────
  u({
    id: 'recon-plane-prop',
    name: 'Avião de reconhecimento monomotor',
    abbr: 'RCN',
    branch: 'air',
    domain: 'air',
    era: 1,
    unitClass: 'AER',
    atk: 0,
    def: 2,
    move: 0,
    range: 2,
    detectability: 'A',
    cost: '20 MAD · 10 FER · 20 $',
    upkeep: { money: 1 },
    producedAt: ['air-base'],
    buildMinutes: 1 * H,
    research: 'aviation',
    recon: true,
    noDamage: true,
    upgradeGroup: 'aviation',
    sidc: 'SFAPMFR--------',
  }),
  u({
    id: 'prop-fighter',
    name: 'Caça a hélice',
    abbr: 'CÇ',
    branch: 'air',
    domain: 'air',
    era: 1,
    unitClass: 'AER',
    atk: 12,
    def: 10,
    move: 0,
    range: 2,
    detectability: 'A',
    cost: '20 MAD · 15 FER · 30 $',
    upkeep: { money: 1 },
    producedAt: ['air-base'],
    buildMinutes: 1 * H,
    research: 'fighter',
    fighter: true,
    airDefenseCoverage: 2,
    upgradeGroup: 'aviation',
    sidc: 'SFAPMFF--------',
  }),
  u({
    id: 'prop-bomber',
    name: 'Bombardeiro monomotor',
    abbr: 'BMB',
    branch: 'air',
    domain: 'air',
    era: 1,
    unitClass: 'AER',
    atk: 2,
    def: 6,
    move: 0,
    range: 2,
    detectability: 'A',
    bombingPower: 5,
    cost: '25 MAD · 20 FER · 35 $',
    upkeep: { money: 1 },
    producedAt: ['air-base'],
    buildMinutes: 1 * H,
    research: 'bombing',
    bomber: true,
    noDamage: true,
    upgradeGroup: 'aviation',
    sidc: 'SFAPMFB--------',
  }),
  u({
    id: 'recon-plane-twin',
    name: 'Avião de reconhecimento bimotor',
    abbr: 'RCN',
    branch: 'air',
    domain: 'air',
    era: 2,
    unitClass: 'AER',
    atk: 0,
    def: 4,
    move: 0,
    range: 4,
    detectability: 'A',
    cost: '15 ALU · 40 $',
    upkeep: { money: 2 },
    producedAt: ['air-base'],
    buildMinutes: 1 * H,
    research: 'twin-engine-aviation',
    recon: true,
    noDamage: true,
    upgradeGroup: 'aviation',
    sidc: 'SFAPMFR--------',
  }),
  u({
    id: 'twin-bomber',
    name: 'Bombardeiro bimotor',
    abbr: 'BMB',
    branch: 'air',
    domain: 'air',
    era: 2,
    unitClass: 'AER',
    atk: 3,
    def: 8,
    move: 0,
    range: 3,
    detectability: 'A',
    bombingPower: 10,
    cost: '25 ALU · 10 AÇO · 60 $',
    upkeep: { money: 2 },
    producedAt: ['air-base'],
    buildMinutes: 1 * H,
    research: 'twin-engine-aviation',
    bomber: true,
    noDamage: true,
    upgradeGroup: 'aviation',
    sidc: 'SFAPMFB--------',
  }),
  u({
    id: 'heavy-bomber',
    name: 'Bombardeiro quadrimotor',
    abbr: 'BMP',
    branch: 'air',
    domain: 'air',
    era: 2,
    unitClass: 'AER',
    atk: 4,
    def: 12,
    move: 0,
    range: 4,
    detectability: 'A',
    bombingPower: 20,
    cost: '40 ALU · 20 AÇO · 100 $',
    upkeep: { money: 3 },
    producedAt: ['air-base'],
    buildMinutes: 1 * H,
    research: 'strategic-bombing',
    bomber: true,
    noDamage: true,
    nuclearCapable: true,
    upgradeGroup: 'aviation',
    sidc: 'SFAPMFB--------',
  }),
  u({
    id: 'cargo-plane',
    name: 'Cargueiro de recursos',
    abbr: 'CRG',
    branch: 'air',
    domain: 'air',
    era: 2,
    unitClass: 'AER',
    atk: 0,
    def: 6,
    move: 0,
    range: null,
    detectability: 'A',
    cost: '30 ALU · 50 $',
    upkeep: { money: 2 },
    producedAt: ['air-base'],
    buildMinutes: 1 * H,
    research: 'cargo-aviation',
    cargo: 'resources',
    noDamage: true,
    capacity: { resources: 100 },
    upgradeGroup: 'aviation',
    sidc: 'SFAPMFC--------',
  }),
  u({
    id: 'transport-plane',
    name: 'Cargueiro de unidades',
    abbr: 'CRG',
    branch: 'air',
    domain: 'air',
    era: 2,
    unitClass: 'AER',
    atk: 0,
    def: 6,
    move: 0,
    range: null,
    detectability: 'A',
    cost: '30 ALU · 60 $',
    upkeep: { money: 2 },
    producedAt: ['air-base'],
    buildMinutes: 1 * H,
    research: 'cargo-aviation',
    cargo: 'units',
    noDamage: true,
    capacity: { troops: 10, vehicles: 2 },
    upgradeGroup: 'aviation',
    sidc: 'SFAPMFC--------',
  }),
  u({
    id: 'jet-fighter',
    name: 'Caça a jato',
    abbr: 'CÇJ',
    branch: 'air',
    domain: 'air',
    era: 3,
    unitClass: 'AER',
    atk: 25,
    def: 20,
    move: 0,
    range: 3,
    detectability: 'M',
    cost: '30 ALU · 10 SEM · 100 $',
    upkeep: { money: 3 },
    producedAt: ['air-base'],
    buildMinutes: 2 * H,
    research: 'jet-propulsion',
    fighter: true,
    airDefenseCoverage: 3,
    upgradeGroup: 'aviation',
    sidc: 'SFAPMFF--------',
  }),
  u({
    id: 'jet-recon-plane',
    name: 'Avião de reconhecimento a jato',
    abbr: 'RCJ',
    branch: 'air',
    domain: 'air',
    era: 3,
    unitClass: 'AER',
    atk: 0,
    def: 8,
    move: 0,
    range: 4,
    rangeOneWay: 6,
    detectability: 'M',
    cost: '25 ALU · 10 SEM · 80 $',
    upkeep: { money: 3 },
    producedAt: ['air-base'],
    buildMinutes: 2 * H,
    research: 'jet-propulsion',
    recon: true,
    noDamage: true,
    upgradeGroup: 'aviation',
    sidc: 'SFAPMFR--------',
  }),
  u({
    id: 'jet-bomber',
    name: 'Bombardeiro a jato de longo alcance',
    abbr: 'BMJ',
    branch: 'air',
    domain: 'air',
    era: 3,
    unitClass: 'AER',
    atk: 5,
    def: 16,
    move: 0,
    range: 4,
    rangeOneWay: 6,
    detectability: 'M',
    bombingPower: 30,
    cost: '50 ALU · 20 SEM · 180 $',
    upkeep: { money: 4 },
    producedAt: ['air-base'],
    buildMinutes: 2 * H,
    research: 'jet-bomber',
    bomber: true,
    noDamage: true,
    nuclearCapable: true,
    upgradeGroup: 'aviation',
    sidc: 'SFAPMFB--------',
  }),
  u({
    id: 'stealth-fighter',
    name: 'Caça a jato stealth',
    abbr: 'CÇS',
    branch: 'air',
    domain: 'air',
    era: 4,
    unitClass: 'AER',
    atk: 30,
    def: 22,
    move: 0,
    range: 3,
    rangeOneWay: 5,
    detectability: 'B',
    cost: '40 ALU · 30 SEM · 250 $',
    upkeep: { money: 5 },
    producedAt: ['air-base'],
    buildMinutes: 2 * H,
    research: 'stealth',
    fighter: true,
    airDefenseCoverage: 3,
    upgradeGroup: 'aviation',
    sidc: 'SFAPMFF--------',
  }),
  u({
    id: 'stealth-bomber',
    name: 'Bombardeiro stealth',
    abbr: 'BMS',
    branch: 'air',
    domain: 'air',
    era: 4,
    unitClass: 'AER',
    atk: 4,
    def: 14,
    move: 0,
    range: 3,
    rangeOneWay: 5,
    detectability: 'B',
    bombingPower: 25,
    cost: '50 ALU · 40 SEM · 300 $',
    upkeep: { money: 5 },
    producedAt: ['air-base'],
    buildMinutes: 2 * H,
    research: 'stealth',
    bomber: true,
    noDamage: true,
    nuclearCapable: true,
    upgradeGroup: 'aviation',
    sidc: 'SFAPMFB--------',
  }),

  // ─── Helicópteros (Base aérea; marcham como o exército) ─────────────
  u({
    id: 'transport-helicopter',
    name: 'Helicóptero de transporte',
    abbr: 'HLT',
    branch: 'helicopter',
    domain: 'land',
    era: 2,
    unitClass: 'HEL',
    atk: 0,
    def: 6,
    move: 3,
    cost: '20 ALU · 40 $',
    upkeep: { money: 1 },
    producedAt: ['air-base'],
    buildMinutes: 45 * MIN,
    research: 'rotary-wing',
    loot: 30,
    noDamage: true,
    capacity: { troops: 10, vehicles: 1 },
    upgradeGroup: 'aviation',
    sidc: 'SFAPMHU--------',
  }),
  u({
    id: 'recon-helicopter',
    name: 'Helicóptero de reconhecimento',
    abbr: 'HLR',
    branch: 'helicopter',
    domain: 'land',
    era: 3,
    unitClass: 'HEL',
    atk: 2,
    def: 6,
    move: 2,
    range: 2,
    detectability: 'M',
    cost: '20 ALU · 5 SEM · 40 $',
    upkeep: { money: 2 },
    producedAt: ['air-base'],
    buildMinutes: 45 * MIN,
    research: 'advanced-helicopters',
    recon: true,
    upgradeGroup: 'aviation',
    sidc: 'SFAPMHR--------',
  }),
  u({
    id: 'attack-helicopter',
    name: 'Helicóptero de combate',
    abbr: 'HLC',
    branch: 'helicopter',
    domain: 'land',
    era: 3,
    unitClass: 'HEL',
    atk: 22,
    def: 10,
    move: 2,
    cost: '25 ALU · 10 SEM · 80 $',
    upkeep: { money: 2 },
    producedAt: ['air-base'],
    buildMinutes: 45 * MIN,
    research: 'advanced-helicopters',
    upgradeGroup: 'aviation',
    sidc: 'SFAPMHA--------',
  }),

  // ─── Aviação naval (Base naval; porta-aviões) ───────────────────────
  u({
    id: 'carrier-fighter',
    name: 'Caça embarcado',
    abbr: 'CÇE',
    branch: 'naval-aviation',
    domain: 'air',
    era: 2,
    unitClass: 'AER',
    atk: 14,
    def: 12,
    move: 0,
    range: 2,
    detectability: 'A',
    cost: '20 ALU · 40 $',
    upkeep: { money: 2 },
    producedAt: ['naval-base'],
    buildMinutes: 1 * H,
    research: 'naval-aviation',
    fighter: true,
    carrierCapable: true,
    airDefenseCoverage: 2,
    upgradeGroup: 'aviation',
    sidc: 'SFAPMFF--------',
  }),
  u({
    id: 'torpedo-bomber',
    name: 'Torpedeiro embarcado',
    abbr: 'TRP',
    branch: 'naval-aviation',
    domain: 'air',
    era: 2,
    unitClass: 'AER',
    atk: 10,
    def: 6,
    move: 0,
    range: 2,
    detectability: 'A',
    // Também leva bombas: bombardeio leve em terra a partir do porta-aviões.
    bombingPower: 5,
    cost: '20 ALU · 35 $',
    upkeep: { money: 2 },
    producedAt: ['naval-base'],
    buildMinutes: 1 * H,
    research: 'naval-aviation',
    multipliers: { NAV: 2.5 },
    bomber: true,
    carrierCapable: true,
    upgradeGroup: 'aviation',
    sidc: 'SFAPMFA--------',
  }),
  u({
    id: 'asw-helicopter',
    name: 'Helicóptero antissubmarino',
    abbr: 'HAS',
    branch: 'naval-aviation',
    domain: 'sea',
    era: 3,
    unitClass: 'HEL',
    atk: 8,
    def: 6,
    move: 0,
    range: 1,
    detectability: 'M',
    cost: '20 ALU · 10 SEM · 50 $',
    upkeep: { money: 2 },
    producedAt: ['naval-base'],
    buildMinutes: 45 * MIN,
    research: 'anti-submarine-warfare',
    multipliers: { SUB: 3, NAV: 0.5 },
    carrierCapable: true,
    detectsSubmarines: true,
    upgradeGroup: 'aviation',
    sidc: 'SFAPMH---------',
  }),

  // ─── Marinha (Base naval) ────────────────────────────────────────────
  u({
    id: 'merchant-ship',
    name: 'Navio mercante',
    abbr: 'MER',
    branch: 'navy',
    domain: 'sea',
    era: 1,
    unitClass: 'NAV',
    atk: 0,
    def: 4,
    move: 3,
    cost: '20 AÇO · 20 $',
    upkeep: { money: 1 },
    producedAt: ['naval-base'],
    buildMinutes: 3 * H,
    research: 'shipbuilding',
    noDamage: true,
    cargo: 'resources',
    capacity: { resources: 200 },
    upgradeGroup: 'navy',
    sidc: 'SFSPXM---------',
  }),
  u({
    id: 'corvette',
    name: 'Corveta',
    abbr: 'CV',
    branch: 'navy',
    domain: 'sea',
    era: 1,
    unitClass: 'NAV',
    atk: 6,
    def: 8,
    move: 4,
    cost: '15 AÇO · 25 $',
    upkeep: { money: 1 },
    producedAt: ['naval-base'],
    buildMinutes: 3 * H,
    research: 'shipbuilding',
    loot: 10,
    recon: true,
    upgradeGroup: 'navy',
    sidc: 'SFSPCP---------',
  }),
  u({
    id: 'destroyer',
    name: 'Contratorpedeiro',
    abbr: 'CT',
    branch: 'navy',
    domain: 'sea',
    era: 1,
    unitClass: 'NAV',
    atk: 14,
    def: 14,
    move: 3,
    cost: '30 AÇO · 50 $',
    upkeep: { money: 2 },
    producedAt: ['naval-base'],
    buildMinutes: 3 * H,
    research: 'destroyer',
    loot: 20,
    lineShip: true,
    detectsSubmarines: true,
    multipliers: { INF: 0.75, BLD: 0.75, ART: 0.75, HEL: 0.5, AER: 0.5, NAV: 1, SUB: 1.5 },
    upgradeGroup: 'navy',
    sidc: 'SFSPCLDD-------',
  }),
  u({
    id: 'landing-ship',
    name: 'Navio de desembarque',
    abbr: 'DES',
    branch: 'navy',
    domain: 'sea',
    era: 1,
    unitClass: 'NAV',
    atk: 0,
    def: 8,
    move: 2,
    cost: '25 AÇO · 30 $',
    upkeep: { money: 1 },
    producedAt: ['naval-base'],
    buildMinutes: 3 * H,
    research: 'shipbuilding',
    noDamage: true,
    capacity: { troops: 10, vehicles: 3, resources: 100 },
    upgradeGroup: 'navy',
    sidc: 'SFSPCA---------',
  }),
  u({
    id: 'submarine',
    name: 'Submarino',
    abbr: 'SB',
    branch: 'navy',
    domain: 'sea',
    era: 1,
    unitClass: 'SUB',
    atk: 16,
    def: 10,
    move: 2,
    cost: '30 AÇO · 50 $',
    upkeep: { money: 2 },
    producedAt: ['naval-base'],
    buildMinutes: 3 * H,
    research: 'submarine',
    upgradeGroup: 'submarines',
    sidc: 'SFUPSC---------',
  }),
  u({
    id: 'recon-submarine',
    name: 'Submarino de reconhecimento',
    abbr: 'SBR',
    branch: 'navy',
    domain: 'sea',
    era: 2,
    unitClass: 'SUB',
    atk: 2,
    def: 6,
    move: 3,
    cost: '25 AÇO · 50 $',
    upkeep: { money: 2 },
    producedAt: ['naval-base'],
    buildMinutes: 3 * H,
    research: 'recon-submarine',
    recon: true,
    upgradeGroup: 'submarines',
    sidc: 'SFUPSC---------',
  }),
  u({
    id: 'frigate',
    name: 'Fragata',
    abbr: 'FG',
    branch: 'navy',
    domain: 'sea',
    era: 2,
    unitClass: 'NAV',
    atk: 12,
    def: 18,
    move: 3,
    cost: '35 AÇO · 10 ALU · 70 $',
    upkeep: { money: 3 },
    producedAt: ['naval-base'],
    buildMinutes: 6 * H,
    research: 'frigate',
    lineShip: true,
    detectsSubmarines: true,
    multipliers: { INF: 0.75, BLD: 0.75, ART: 0.75, HEL: 0.5, AER: 1, NAV: 1, SUB: 2.5 },
    capacity: { asw: 1 },
    upgradeGroup: 'navy',
    sidc: 'SFSPCLFF-------',
  }),
  u({
    id: 'cruiser',
    name: 'Cruzador',
    abbr: 'CR',
    branch: 'navy',
    domain: 'sea',
    era: 2,
    unitClass: 'NAV',
    atk: 24,
    def: 22,
    move: 3,
    cost: '60 AÇO · 120 $',
    upkeep: { money: 4 },
    producedAt: ['naval-base'],
    buildMinutes: 6 * H,
    research: 'cruiser',
    loot: 30,
    lineShip: true,
    capacity: { asw: 2, missiles: 4 },
    missileLoad: 'cruise-missile',
    upgradeGroup: 'navy',
    sidc: 'SFSPCLCC-------',
  }),
  u({
    id: 'battleship',
    name: 'Encouraçado',
    abbr: 'EN',
    branch: 'navy',
    domain: 'sea',
    era: 2,
    unitClass: 'NAV',
    atk: 40,
    def: 40,
    move: 2,
    cost: '120 AÇO · 250 $',
    upkeep: { money: 8 },
    producedAt: ['naval-base'],
    buildMinutes: 12 * H,
    research: 'battleship',
    lineShip: true,
    multipliers: { INF: 1.5, BLD: 1.5, ART: 1.5, HEL: 0.5, AER: 0.5, NAV: 1, SUB: 0 },
    upgradeGroup: 'navy',
    sidc: 'SFSPCLBB-------',
  }),
  u({
    id: 'aircraft-carrier',
    name: 'Porta-aviões',
    abbr: 'PA',
    branch: 'navy',
    domain: 'sea',
    era: 2,
    unitClass: 'NAV',
    atk: 4,
    def: 30,
    move: 2,
    cost: '150 AÇO · 30 ALU · 350 $',
    upkeep: { money: 10 },
    producedAt: ['naval-base'],
    buildMinutes: 12 * H,
    research: 'aircraft-carrier',
    capacity: { aircraft: 12 },
    upgradeGroup: 'navy',
    sidc: 'SFSPCLCV-------',
  }),
  u({
    id: 'nuclear-submarine',
    name: 'Submarino nuclear',
    abbr: 'SN',
    branch: 'navy',
    domain: 'sea',
    era: 3,
    unitClass: 'SUB',
    atk: 28,
    def: 20,
    move: 4,
    cost: '60 AÇO · 20 SEM · 5 UEN · 250 $',
    upkeep: { money: 6 },
    producedAt: ['naval-base'],
    buildMinutes: 6 * H,
    research: 'nuclear-propulsion',
    capacity: { missiles: 4 },
    missileLoad: 'cruise-missile',
    upgradeGroup: 'submarines',
    sidc: 'SFUPSN---------',
  }),
  u({
    id: 'ssbn',
    name: 'SSBN',
    abbr: 'SSBN',
    branch: 'navy',
    domain: 'sea',
    era: 4,
    unitClass: 'SUB',
    atk: 10,
    def: 24,
    move: 3,
    cost: '80 AÇO · 30 SEM · 10 UEN · 400 $',
    upkeep: { money: 8 },
    producedAt: ['naval-base'],
    buildMinutes: 12 * H,
    research: 'ssbn',
    capacity: { missiles: 4, warheads: 2 },
    missileLoad: 'slbm',
    upgradeGroup: 'submarines',
    sidc: 'SFUPSNB--------',
  }),

  // ─── Inteligência ────────────────────────────────────────────────────
  u({
    id: 'agent',
    name: 'Agente',
    abbr: 'AGT',
    branch: 'intel',
    domain: 'none',
    era: 1,
    unitClass: null,
    atk: 0,
    def: 0,
    move: 0,
    cost: '10 ALI · 30 $',
    upkeep: { money: 2 },
    producedAt: ['intelligence-agency'],
    buildMinutes: 10 * MIN,
    research: 'secret-service',
    sidc: 'SFFP-----------',
  }),

  // ─── Mísseis (consumíveis) ───────────────────────────────────────────
  u({
    id: 'cruise-missile',
    name: 'Míssil de cruzeiro',
    abbr: 'MC',
    branch: 'missile',
    domain: 'none',
    era: 3,
    unitClass: null,
    atk: 0,
    def: 0,
    move: 0,
    range: 4,
    detectability: 'B',
    bombingPower: 30,
    cost: '10 ALU · 10 SEM · 5 CMB · 40 $',
    producedAt: ['naval-base'],
    buildMinutes: 2 * H,
    research: 'tactical-missiles',
    consumable: true,
    sidc: 'SFAPWM---------',
  }),
  u({
    id: 'mrbm',
    name: 'Míssil balístico de médio alcance',
    abbr: 'MBM',
    branch: 'missile',
    domain: 'none',
    era: 3,
    unitClass: null,
    atk: 0,
    def: 0,
    move: 0,
    range: 8,
    bombingPower: 60,
    cost: '20 AÇO · 15 SEM · 20 CMB · 100 $',
    producedAt: ['missile-silo'],
    buildMinutes: 6 * H,
    research: 'rockets',
    consumable: true,
    nuclearCapable: true,
    sidc: 'SFAPWMB--------',
  }),
  u({
    id: 'slbm',
    name: 'SLBM',
    abbr: 'SLBM',
    branch: 'missile',
    domain: 'none',
    era: 4,
    unitClass: null,
    atk: 0,
    def: 0,
    move: 0,
    range: 8,
    bombingPower: 60,
    cost: '20 AÇO · 15 SEM · 20 CMB · 120 $',
    producedAt: ['naval-base'],
    buildMinutes: 6 * H,
    research: 'ssbn',
    consumable: true,
    nuclearCapable: true,
    sidc: 'SFAPWMB--------',
  }),
  u({
    id: 'icbm',
    name: 'ICBM',
    abbr: 'ICBM',
    branch: 'missile',
    domain: 'none',
    era: 4,
    unitClass: null,
    atk: 0,
    def: 0,
    move: 0,
    range: null,
    bombingPower: 90,
    cost: '40 AÇO · 30 SEM · 40 CMB · 250 $',
    producedAt: ['missile-silo'],
    buildMinutes: 12 * H,
    research: 'icbm',
    consumable: true,
    nuclearCapable: true,
    sidc: 'SFAPWMB--------',
  }),
  u({
    id: 'nuclear-warhead',
    name: 'Ogiva nuclear',
    abbr: 'NUC',
    branch: 'missile',
    domain: 'none',
    era: 3,
    unitClass: null,
    atk: 0,
    def: 0,
    move: 0,
    cost: '20 UEN · 50 SEM · 500 $',
    producedAt: ['missile-silo', 'naval-base', 'air-base'],
    buildMinutes: 24 * H,
    research: 'atomic-bomb',
    consumable: true,
    sidc: 'SFAPWM---------',
  }),
  u({
    id: 'asat-missile',
    name: 'Míssil antissatélite',
    abbr: 'ASAT',
    branch: 'missile',
    domain: 'none',
    era: 4,
    unitClass: null,
    atk: 0,
    def: 0,
    move: 0,
    cost: '20 ALU · 30 SEM · 30 CMB · 300 $',
    producedAt: ['space-agency'],
    buildMinutes: 12 * H,
    research: 'asat',
    consumable: true,
    sidc: 'SFAPWM---------',
  }),

  // ─── Espaço ──────────────────────────────────────────────────────────
  u({
    id: 'inclined-satellite',
    name: 'Satélite de reconhecimento de órbita inclinada',
    abbr: 'SAT',
    branch: 'space',
    domain: 'none',
    era: 3,
    unitClass: null,
    atk: 0,
    def: 0,
    move: 0,
    cost: '20 ALU · 20 SEM · 40 CMB · 300 $',
    producedAt: ['space-agency'],
    buildMinutes: 24 * H,
    research: 'space-program',
    lifetimeTurns: 40,
    sidc: 'SFPPS----------',
  }),
  u({
    id: 'polar-satellite',
    name: 'Satélite de reconhecimento de órbita polar',
    abbr: 'SAT',
    branch: 'space',
    domain: 'none',
    era: 3,
    unitClass: null,
    atk: 0,
    def: 0,
    move: 0,
    cost: '30 ALU · 30 SEM · 50 CMB · 400 $',
    producedAt: ['space-agency'],
    buildMinutes: 24 * H,
    research: 'polar-orbit',
    lifetimeTurns: 40,
    sidc: 'SFPPS----------',
  }),
  u({
    id: 'geo-satellite',
    name: 'Satélite de reconhecimento estacionário',
    abbr: 'SAT',
    branch: 'space',
    domain: 'none',
    era: 4,
    unitClass: null,
    atk: 0,
    def: 0,
    move: 0,
    cost: '40 ALU · 50 SEM · 80 CMB · 600 $',
    producedAt: ['space-agency'],
    buildMinutes: 24 * H,
    research: 'geostationary-satellite',
    lifetimeTurns: 40,
    sidc: 'SFPPS----------',
  }),

  // ─── Neutro ─────────────────────────────────────────────────────────
  u({
    id: 'militia',
    name: 'Milícia',
    abbr: 'MIL',
    branch: 'neutral',
    domain: 'land',
    era: 1,
    unitClass: 'INF',
    atk: 6,
    def: 10,
    move: 0,
    cost: '0 $',
    producedAt: [],
    buildMinutes: 0,
    research: null,
    sidc: 'SNGPUCI--------',
  }),
];

export const UNIT_BY_ID: Readonly<Record<string, UnitDef>> = Object.fromEntries(
  UNITS.map((def) => [def.id, def]),
);
export const UNIT_IDS: readonly string[] = UNITS.map((def) => def.id);

export function getUnit(id: string): UnitDef {
  const def = UNIT_BY_ID[id];
  if (!def) throw new Error(`Unidade desconhecida: ${id}`);
  return def;
}

/** Matriz de multiplicadores padrão (atacante × alvo). */
export const CLASS_MULTIPLIERS: Record<UnitClass, Record<UnitClass, number>> = {
  INF: { INF: 1, BLD: 0.5, ART: 1, HEL: 0.25, AER: 0, NAV: 0, SUB: 0 },
  BLD: { INF: 1.5, BLD: 1, ART: 1.5, HEL: 0, AER: 0, NAV: 0, SUB: 0 },
  ART: { INF: 1.5, BLD: 1, ART: 1, HEL: 0, AER: 0, NAV: 0, SUB: 0 },
  HEL: { INF: 1, BLD: 2, ART: 1, HEL: 1, AER: 0, NAV: 0, SUB: 0 },
  AER: { INF: 0.5, BLD: 0.5, ART: 0.5, HEL: 1.5, AER: 1, NAV: 0.5, SUB: 0 },
  NAV: { INF: 0.75, BLD: 0.75, ART: 0.75, HEL: 0.5, AER: 0.5, NAV: 1, SUB: 0 },
  SUB: { INF: 0, BLD: 0, ART: 0, HEL: 0, AER: 0, NAV: 1.5, SUB: 0.5 },
};

/** Multiplicador da unidade contra uma classe alvo (considera exceções por unidade). */
export function unitMultiplier(def: UnitDef, target: UnitClass): number {
  if (def.multipliers) return def.multipliers[target] ?? 0;
  if (!def.unitClass) return 0;
  return CLASS_MULTIPLIERS[def.unitClass][target];
}

/** Categorias de transporte de tropas: INF é "tropa", o resto terrestre é "veículo". */
export function isTroop(def: UnitDef): boolean {
  return def.domain === 'land' && def.unitClass === 'INF';
}

export function isVehicle(def: UnitDef): boolean {
  return def.domain === 'land' && def.unitClass !== 'INF' && def.unitClass !== 'HEL';
}
